<template>
  <div class="h-full flex flex-col">
    <!-- Filter Bar -->
    <div class="px-3 py-2 flex items-center justify-between">
      <div class="flex items-center gap-2">
        <i class="fas fa-sort text-xs text-gray-400"></i>
        <select 
          v-model="filterSort" 
          class="text-xs border-0 bg-transparent text-gray-700 dark:text-gray-300 font-medium focus:ring-0 cursor-pointer hover:text-blue-600 transition-colors"
        >
          <option value="expiry">Expiring Soon</option>
          <option value="newest">Newest First</option>
          <option value="oldest">Oldest First</option>
        </select>
      </div>
    </div>

    <!-- Quotations List -->
    <div class="flex-1 overflow-y-auto px-3 space-y-2">
      <div v-for="quotation in paginatedQuotations" :key="quotation.id"
        class="group relative p-2.5 rounded-lg bg-gradient-to-br from-white/50 to-white/30 dark:from-dark-700/50 dark:to-dark-700/30 border border-blue-200/50 dark:border-blue-800/30 hover:border-blue-300 dark:hover:border-blue-700 hover:shadow-md transition-all duration-200 cursor-pointer"
        @click="viewQuotation(quotation.id)">
        
        <div class="space-y-1">
          <!-- Top Row: Client Name and Status Badge -->
          <div class="flex items-start justify-between gap-2">
            <h4 class="text-xs font-semibold text-gray-900 dark:text-white truncate flex-1" :title="quotation.clientName">
              {{ truncateClientName(quotation.clientName, 15) }}
            </h4>
            <span :class="getQuotationStatusClass(quotation.status)" class="text-[9px] px-1.5 py-0.5 rounded-full font-semibold whitespace-nowrap flex-shrink-0">
              {{ quotation.status }}
            </span>
          </div>
          
          <!-- Quote Number and Amount -->
          <div class="flex items-center justify-between">
            <span class="text-blue-600 dark:text-blue-400 font-medium text-[10px] hover:underline">
              Quote #{{ quotation.quoteNumber }}
            </span>
            <span class="text-xs font-bold text-blue-600 dark:text-blue-400">{{ formatCurrency(quotation.amount) }}</span>
          </div>

          <!-- Dates -->
          <div class="flex items-center justify-between text-[9px] text-gray-500 dark:text-gray-400">
            <span class="flex items-center gap-1">
              <i class="fas fa-calendar-alt"></i>
              {{ formatDate(quotation.quoteDate) }}
            </span>
            <span class="flex items-center gap-1">
              <i class="fas fa-clock"></i>
              {{ formatDate(quotation.validUntil) }}
            </span>
          </div>
        </div>

        <!-- Hover Effect -->
        <div class="absolute inset-0 rounded-lg bg-gradient-to-br from-blue-400/0 to-cyan-400/0 group-hover:from-blue-400/5 group-hover:to-cyan-400/5 transition-all pointer-events-none"></div>
      </div>

      <!-- Empty State -->
      <div v-if="quotations.length === 0" class="flex flex-col items-center justify-center py-8 text-center">
        <div class="w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900/20 flex items-center justify-center mb-2">
          <i class="fas fa-file-invoice text-lg text-blue-600 dark:text-blue-400"></i>
        </div>
        <p class="text-xs font-medium text-gray-900 dark:text-white">No quotations</p>
        <p class="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">Create your first quotation</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { formatCurrency } from '@/utils/formatters';

const props = defineProps({
  quotations: {
    type: Array,
    default: () => []
  },
  defaultSort: {
    type: String,
    default: 'expiry'
  },
  perPage: {
    type: Number,
    default: 5
  }
});

const emit = defineEmits(['view-quotation']);

const filterSort = ref(props.defaultSort);
const currentPage = ref(1);

// Reset pagination when filters change
watch(filterSort, () => {
  currentPage.value = 1;
}, { immediate: true });

// Apply filters and sorting
const filteredData = computed(() => {
  let sorted = [...props.quotations];
  
  switch (filterSort.value) {
    case 'expiry':
      sorted.sort((a, b) => {
        const daysA = a.daysUntilExpiry || 0;
        const daysB = b.daysUntilExpiry || 0;
        return daysA - daysB; // Sort by days until expiry (ascending)
      });
      break;
    case 'newest':
      sorted.sort((a, b) => new Date(b.quoteDate) - new Date(a.quoteDate));
      break;
    case 'oldest':
      sorted.sort((a, b) => new Date(a.quoteDate) - new Date(b.quoteDate));
      break;
    case 'amount_high':
      sorted.sort((a, b) => parseFloat(b.amount) - parseFloat(a.amount));
      break;
    case 'amount_low':
      sorted.sort((a, b) => parseFloat(a.amount) - parseFloat(b.amount));
      break;
  }
  
  return sorted;
});

// Paginate the data
const paginatedQuotations = computed(() => {
  const startIndex = (currentPage.value - 1) * props.perPage;
  return filteredData.value.slice(startIndex, startIndex + props.perPage);
});

const viewQuotation = (quotationId) => {
  emit('view-quotation', quotationId);
};

const formatDate = (date) => {
  if (!date) return 'N/A';
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

const getQuotationStatusClass = (status) => {
  const statusMap = {
    'PENDING': 'bg-yellow-100 text-yellow-800',
    'SYSTEM_APPROVED': 'bg-blue-100 text-blue-800',
    'CLIENT_APPROVED': 'bg-green-100 text-green-800',
    'REJECTED': 'bg-red-100 text-red-800',
    'EXPIRED': 'bg-purple-100 text-purple-800',
    'DELETED': 'bg-red-50 text-red-600',
    'DRAFT': 'bg-gray-100 text-gray-800'
  };
  
  // Check for normalized versions of the status strings
  if (!status) return 'bg-gray-100 text-gray-800';
  
  // Account for status variations (snake_case vs display text)
  const normalizedStatus = status.toUpperCase().replace(' ', '_');
  
  return statusMap[normalizedStatus] || 'bg-gray-100 text-gray-800';
};

const truncateClientName = (name, length = 15) => {
  if (!name) return '';
  return name.length > length ? name.substring(0, length) + '...' : name;
};
</script> 